<template>
  <div class="mb-8">
    <h3
      class="font-roboto text-xl font-bold mb-2"
      id="recommendedReviewersTitle">
      Recommended reviewers
    </h3>
    <ul class="space-y-4 list-none p-0 mb-4">
      <ReviewerListItem
        v-for="reviewer in recommendedReviewers"
        :key="reviewer.id"
        :reviewer="reviewer"
        :showReason="true"
        :showEdit="false"
      />
    </ul>
    <AddFileCard
      v-if="recommendedReviewers.length < 2"
      anchor-link="/submission/review-editorial"
      scroll-to-id="reviewerInformationTitle"
      :label="recommendedReviewers.length  === 1 ? 'One more recommended reviewer is required' : 'Required'"
      data-testid="add-recommended-reviewer-card"
    />
    <InlineWarningMessage
      v-if="recommendedReviewers.length < 2 && requires2RecommendedErrorMessage"
      :errorMessage="requires2RecommendedErrorMessage"
      errorTestId="requires-two-reviewers"
    />
  </div>

  <div class="mb-8">
    <h3 class="font-roboto text-xl font-bold mb-2">
      Non-preferred reviewers
    </h3>
    <div
      role="status"
      class="bg-faint-grey rounded-md p-4 mb-4"
      v-if="!opposedReviewers.length">
      Not provided (optional)
    </div>
    <ul
      v-else
      class="space-y-3 list-none p-0 mb-4">
      <ReviewerListItem
        v-for="reviewer in opposedReviewers"
        :key="reviewer.id"
        :reviewer="reviewer"
        :showReason="true"
        :showEdit="false"
      />
    </ul>
  </div>

  <section class="mb-8">
    <h3
      id="titleTPR"
      class="font-roboto text-xl font-bold mb-2">
      Transparent peer review
    </h3>
    <p
      v-if="tprChoice"
      id="varTPR"
      class="bg-faint-grey rounded-lg p-4">
      {{ tprChoice }}
    </p>
    <AddFileCard
      v-if="transparentPeerReview === 'Unknown'"
      anchor-link="/submission/review-editorial"
      scroll-to-id="pnlReviewTypeQuestionSelector"
      data-testid="add-transparent-peer-review-card"
    />
    <InlineWarningMessage
      v-if="TPRErrorMessage"
      :errorMessage="TPRErrorMessage"
      errorTestId="transparent-peer-review"/>
  </section>

  <div v-if="authorDecidedEditorialOffices">
    <section
      class="mb-8"
      id="2-associate-editors">
      <h3
        id="associateEditorTitle"
        class="font-roboto text-xl font-bold mb-2">
        Associate Editor
      </h3>

      <NotificationComponent
        v-if="hasError"
        data-testid="failed-to-load-associate-editors"
        class="mb-8"
        role="alert"
        variant="error"
        heading-text="Failed to load Associate Editors"
        :heading-level="1">
        <template #body>
          <p>
            Unable to retrieve Associate Editor information. Please retry or <a
              data-testid="email-customer-service-get-associate-editors"
              class="underline text-royal-blue-100 hover:text-royal-blue-160 focus:text-royal-blue-160 visited:text-visited"
              id="linkCustomerServicesAssociateEditors"
              href="mailto:manuscriptsupport@rsc.org?subject=RSC%20Submissions:%20Unable%20to%20retrieve%20Associate%20Editor%20information">email customer services</a> if the issue persists.
          </p>
        </template>
      </NotificationComponent>

      <template v-if="!isLoading && !hasError">
        <div
          v-for="(editor, i) in selectedEditors"
          :key="i"
          class="bg-faint-grey rounded-lg p-4 mb-4"
        >
          <p>
            <span :id="getEditorialNameId(i)">
              <strong>{{ editor.name }}</strong>
            </span>
            <br />
            {{ editor.area }},
            <br />
            <span :id="getEditorialAffiliationId(i)">{{ editor.affiliation }}</span>
          </p>
        </div>
        <AddFileCard
          v-if="selectedEditors.length < 2"
          anchor-link="/submission/review-editorial"
          scroll-to-id="editorial_office"
          :label="!selectedEditors.length ? 'Required' : 'One more Associate Editor is required'"
          data-testid="add-associate-editor-card"
        />
        <InlineWarningMessage
          v-if="selectedEditors.length <= 1 && associateEditorErrorMessage"
          :errorMessage="associateEditorErrorMessage"
          errorTestId="associate-editor"
        />
      </template>
    </section>
  </div>

  <div v-else>
    <section class="mb-8">
      <h3
        id="associateEditorTitle"
        class="font-roboto text-xl font-bold mb-4">
        Associate Editor
      </h3>
      <div class="bg-faint-grey rounded-lg p-4 mb-4">
        <p id="varEditorialOffice">
          I am happy for the editorial team to select the most appropriate Associate Editor.
        </p>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { EditorialOffice, EditorialOfficeSelectionMode } from '@/types'
import submissionApi from '../../api/submission-api'
import { useStore } from 'vuex'
import { useField } from 'vee-validate'
import InlineWarningMessage from '../common/InlineWarningMessage.vue'
import ReviewerListItem from '../reviewer/ReviewerListItem.vue'
import NotificationComponent from '../ui-components/NotificationComponent.vue'
import { trackEvent } from '@/services/gtm'
import AddFileCard from '../common/AddFileCard.vue'
import { useReviewerManagement } from '@/composables/useReviewerManagement'

const store = useStore()
const allEditors = ref([] as EditorialOffice[])

const { recommendedReviewers, opposedReviewers, transparentPeerReview, getTPRChoiceText } = useReviewerManagement()
const hasError = ref(false)
const isLoading = ref(false)

const trackAssociateEditorsErrorEvent = (error) => {
  trackEvent({
    category: 'Server Errors',
    action: 'error',
    event: 'associateEditorsError',
    errorType: error?.response?.status ? `HTTP${error.response.status}` : 'UnknownError',
    errorDetails: error?.message || 'No error message available',
    submissionId: store.state.activeSubmission.submissionId,
  })
}

onMounted(async () => {
  isLoading.value = true
  hasError.value = false

  try {
    const resp = await submissionApi.getEditorialOfficeIds()
    allEditors.value = resp.data
  }
  catch (error) {
    trackAssociateEditorsErrorEvent(error)
    hasError.value = true
  }
  finally {
    isLoading.value = false
  }
})

function findMatch(editorId: string): EditorialOffice | undefined {
  return allEditors.value.find((ed: { peerReviewIdentifier: string }) => ed.peerReviewIdentifier === editorId)
}
function getEditorialNameId(index: number): string {
  return 'editorialName-' + index
}
function getEditorialAffiliationId(index: number): string {
  return 'editorialAffiliation-' + index
}

const selectedEditors = computed((): EditorialOffice[] => {
  return store.state.activeSubmission.editorialOfficeIds
    .map(findMatch)
    .filter((item: EditorialOffice | undefined): item is EditorialOffice => Boolean(item))
})

const authorDecidedEditorialOffices = computed((): boolean => {
  return store.state.editorialOfficeSelectionMode === EditorialOfficeSelectionMode.AuthorDecided
})

const tprChoice = computed((): string => {
  return getTPRChoiceText(transparentPeerReview.value)
})

const { errorMessage: TPRErrorMessage, validate: validateTPR } = useField(
  'input-transparent-peer-review',
  'transparent-peer-review-option-required',
  {
    initialValue: transparentPeerReview.value,
  },
)

const { errorMessage: associateEditorErrorMessage, validate: validateAssociateEditor } = useField(
  'input-associate-editors',
  'at-least-two-associate-editors-required',
  {
    initialValue: selectedEditors.value,
  },
)

const { errorMessage: requires2RecommendedErrorMessage, validate: validateRequires2Recommended } = useField(
  'input-recommended-reviewers',
  'at-least-two-recommended-reviewers-required',
  {
    initialValue: recommendedReviewers.value,
  },
)

watch(() => store.state.submissionAttemptCounter, () => {
  validateTPR()
  validateAssociateEditor()
  validateRequires2Recommended()
})
</script>
